import React from "react";
import {
  Box,
  Heading,
  Text,
  VStack,
  Container,
  Avatar,
  HStack,
  Icon,
  Badge,
  Flex,
  keyframes,
  useColorModeValue
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { FaUser, FaLock, FaToolbox, FaCog } from "react-icons/fa";

// Animation keyframes
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const glow = keyframes`
  0% { box-shadow: 0 0 0px rgba(79, 209, 197, 0.3); }
  50% { box-shadow: 0 0 20px rgba(79, 209, 197, 0.6); }
  100% { box-shadow: 0 0 0px rgba(79, 209, 197, 0.3); }
`;

const MotionBox = motion(Box);

export default function Profile() {
  // Colors that adapt to theme mode
  const bgColor = useColorModeValue("white", "gray.800");
  const accentColor = useColorModeValue("blue.500", "blue.300");
  const cardBg = useColorModeValue("gray.50", "gray.700");
  const textColor = useColorModeValue("gray.700", "whiteAlpha.900");
  
  // Features that will be available in the profile
  const profileFeatures = [
    { name: "Account Settings", icon: FaUser, delay: 0.3 },
    { name: "Security Options", icon: FaLock, delay: 0.4 },
    { name: "Customization Tools", icon: FaToolbox, delay: 0.5 },
    { name: "Preferences", icon: FaCog, delay: 0.6 }
  ];

  return (
    <Container maxW="container.xl" py={10}>
      <VStack spacing={10} align="stretch">
        <Box textAlign="center" position="relative">
          {/* Background decorative elements */}
          <Box
            position="absolute"
            top="-20px"
            left="0"
            right="0"
            height="40px"
            bgGradient="linear(to-r, blue.400, purple.500)"
            opacity={0.1}
            borderRadius="md"
            transform="skewY(-1deg)"
          />
          <Box
            position="absolute"
            bottom="-20px"
            left="0"
            right="0"
            height="40px"
            bgGradient="linear(to-r, purple.500, blue.400)"
            opacity={0.1}
            borderRadius="md"
            transform="skewY(1deg)"
          />

          {/* Main content */}
          <MotionBox 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            p={10}
            bg={bgColor} 
            borderRadius="2xl" 
            boxShadow="xl"
            position="relative"
            overflow="hidden"
          >
            <Box 
              position="absolute" 
              top={0} 
              left={0} 
              right={0} 
              height="6px" 
              bgGradient="linear(to-r, blue.400, purple.500, blue.600)" 
            />

            <VStack spacing={8}>
              <Box
                animation={`${float} 4s ease-in-out infinite`}
                padding={3}
                borderRadius="full"
              >
                <Avatar 
                  size="2xl" 
                  name="User Profile" 
                  src="https://bit.ly/broken-link"
                  bg={accentColor}
                  animation={`${glow} 3s infinite`}
                  mb={2}
                  icon={<Icon as={FaUser} color="white" boxSize={12} />}
                />
              </Box>
              
              <Heading 
                size="2xl" 
                bgClip="text"
                bgGradient="linear(to-r, blue.400, purple.500)" 
                fontWeight="bold"
                letterSpacing="tight"
              >
                User Profile
              </Heading>
              
              <Text 
                fontSize="xl"
                color={textColor}
                maxW="600px"
              >
                We're building a comprehensive profile management system where you can customize 
                your preferences, manage security settings, and personalize your experience.
              </Text>
              
              <HStack>
                <Badge 
                  colorScheme="blue" 
                  fontSize="lg" 
                  px={4} 
                  py={2}
                  borderRadius="full"
                  textTransform="capitalize"
                  fontWeight="medium"
                >
                  Coming Soon
                </Badge>
              </HStack>
            </VStack>
          </MotionBox>
        </Box>
        
        {/* Feature cards */}
        <Flex wrap="wrap" justify="center" gap={6} mt={6}>
          {profileFeatures.map((feature, index) => (
            <MotionBox 
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: feature.delay }}
              bg={cardBg}
              p={6}
              borderRadius="xl"
              boxShadow="md"
              width={{ base: "full", sm: "200px" }}
              textAlign="center"
              borderTop="4px solid"
              borderTopColor={accentColor}
            >
              <VStack spacing={3}>
                <Icon as={feature.icon} boxSize={8} color={accentColor} />
                <Text fontWeight="bold">{feature.name}</Text>
              </VStack>
            </MotionBox>
          ))}
        </Flex>
        
        {/* Footer text */}
        <Box textAlign="center" mt={6} color="gray.500">
          <Text fontSize="sm">
            Our team is working to bring you these features very soon!
          </Text>
        </Box>
      </VStack>
    </Container>
  );
}